import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import API from "../lib/api";
import {
  CheckSquare, Loader2, Users, AlertTriangle, Search, ChevronLeft, ChevronRight,
  Filter, X, Wand2,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const FINDING_REVIEW_STORAGE_PREFIX = "zentis:findingReviews:";
const REVIEW_STATES = { accepted: "accepted" };
const PAGE_SIZE = 25;
const SEVERITIES = ["Critical", "High", "Medium", "Low", "Observation"];

const formatAgentStyleIdentifier = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  const teamSqlMatch = text.match(/^team_sql_([a-z_]+?)(?:_\d+)?(?:_[a-f0-9]{6,})?$/i);
  if (teamSqlMatch?.[1]) return teamSqlMatch[1];
  return text;
};

const severityOf = (f) => f.final_severity || f.suggested_severity || f.severity || "Medium";
const suggestedOf = (f) => f.suggested_severity || f.severity || "Medium";

export default function Phase9Classification() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun, findings: contextFindings = [], fetchFindings } = useAudit();
  const [findings, setFindings] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewedFindingMap, setReviewedFindingMap] = useState({});

  // The severity each finding arrived with. An override reason is required only
  // when the auditor moves a finding away from this, not when the backend's
  // final_severity happens to differ from its own suggested_severity. Comparing
  // two server fields made the page unfinalisable without anyone touching it.
  const [baselineSeverity, setBaselineSeverity] = useState({});

  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [changedOnly, setChangedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [bulkSeverity, setBulkSeverity] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const rowRefs = useRef({});

  const normalizeReviewStatus = (value) => String(value || "").trim().toLowerCase();

  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData(data);
    } catch (e) {
      console.error("Failed to fetch team data:", e);
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchTeamData();
    if (contextFindings.length === 0) fetchFindings();
  }, [fetchTeamData, contextFindings.length, fetchFindings]);

  useEffect(() => {
    if (contextFindings && contextFindings.length > 0) {
      setFindings(contextFindings.filter((f) => {
        if (f.suppressed) return false;
        const reviewedStatus =
          reviewedFindingMap[f.id]?.status ?? f.review_status ?? REVIEW_STATES.accepted;
        return normalizeReviewStatus(reviewedStatus) === REVIEW_STATES.accepted;
      }));
    }
  }, [contextFindings, reviewedFindingMap]);

  // Record the arrival severity once per finding.
  useEffect(() => {
    if (findings.length === 0) return;
    setBaselineSeverity((prev) => {
      let dirty = false;
      const next = { ...prev };
      findings.forEach((f) => {
        if (next[f.id] === undefined) {
          next[f.id] = severityOf(f);
          dirty = true;
        }
      });
      return dirty ? next : prev;
    });
  }, [findings]);

  useEffect(() => {
    if (!auditRun?.id) return;
    try {
      const raw = localStorage.getItem(`${FINDING_REVIEW_STORAGE_PREFIX}${auditRun.id}`);
      setReviewedFindingMap(raw ? JSON.parse(raw) : {});
    } catch {
      setReviewedFindingMap({});
    }
  }, [auditRun?.id]);

  const isTeamMode = teamData?.audit_mode === "team";
  const consolidatedFindings = teamData?.consolidated_findings || [];
  const hasAnyFindings = findings.length > 0 || consolidatedFindings.length > 0;

  const wasChangedByAuditor = useCallback((f) => {
    const base = baselineSeverity[f.id];
    if (base === undefined) return false;
    return severityOf(f) !== base;
  }, [baselineSeverity]);

  const needsReason = useCallback(
    (f) => wasChangedByAuditor(f) && !String(f.override_reason || "").trim(),
    [wasChangedByAuditor],
  );

  const updateSeverity = (id, severity) => {
    setFindings((prev) => prev.map((f) => (f.id === id ? { ...f, final_severity: severity } : f)));
  };
  const updateOverrideReason = (id, overrideReason) => {
    setFindings((prev) => prev.map((f) => (f.id === id ? { ...f, override_reason: overrideReason } : f)));
  };

  const sevCounts = useMemo(() => {
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0, Observation: 0 };
    findings.forEach((f) => {
      const s = severityOf(f);
      if (counts[s] !== undefined) counts[s] += 1;
    });
    consolidatedFindings.forEach((cf) => {
      if (counts[cf.severity] !== undefined) counts[cf.severity] += 1;
    });
    return counts;
  }, [findings, consolidatedFindings]);

  const blocking = useMemo(() => findings.filter(needsReason), [findings, needsReason]);
  const changedCount = useMemo(
    () => findings.filter(wasChangedByAuditor).length,
    [findings, wasChangedByAuditor],
  );

  const filtered = useMemo(() => {
    let list = [...findings].sort((a, b) => {
      const na = !!(a.evidence_narrative || a.summary || a.finding_text);
      const nb = !!(b.evidence_narrative || b.summary || b.finding_text);
      if (na && !nb) return -1;
      if (!na && nb) return 1;
      return 0;
    });
    if (severityFilter !== "all") list = list.filter((f) => severityOf(f) === severityFilter);
    if (issuesOnly) list = list.filter(needsReason);
    if (changedOnly) list = list.filter(wasChangedByAuditor);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((f) => {
        const haystack = [
          f.title, f.finding_title, f.finding_text, f.finding_description, f.description,
          f.control_ref, f.control_id, f.evidence_narrative, f.summary,
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }
    return list;
  }, [findings, severityFilter, issuesOnly, changedOnly, search, needsReason, wasChangedByAuditor]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => { setPage(0); }, [severityFilter, issuesOnly, changedOnly, search]);

  const jumpToFirstIssue = () => {
    if (blocking.length === 0) return;
    setIssuesOnly(true);
    setSeverityFilter("all");
    setChangedOnly(false);
    setSearch("");
    setPage(0);
    setTimeout(() => {
      const node = rowRefs.current[blocking[0].id];
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const applyBulkSeverity = () => {
    if (!bulkSeverity || filtered.length === 0) return;
    const ids = new Set(filtered.map((f) => f.id));
    setFindings((prev) => prev.map((f) => (ids.has(f.id) ? { ...f, final_severity: bulkSeverity } : f)));
    toast.success(`Set ${filtered.length} finding${filtered.length === 1 ? "" : "s"} to ${bulkSeverity}.`);
  };

  const applyBulkReason = () => {
    const reason = bulkReason.trim();
    if (!reason) return;
    const targets = filtered.filter(wasChangedByAuditor);
    if (targets.length === 0) {
      toast.info("No changed findings in the current view to apply a reason to.");
      return;
    }
    const ids = new Set(targets.map((f) => f.id));
    setFindings((prev) => prev.map((f) => (ids.has(f.id) ? { ...f, override_reason: reason } : f)));
    toast.success(`Applied the reason to ${targets.length} changed finding${targets.length === 1 ? "" : "s"}.`);
  };

  const clearFilters = () => {
    setSeverityFilter("all");
    setIssuesOnly(false);
    setChangedOnly(false);
    setSearch("");
  };

  const handleFinalize = async () => {
    if (blocking.length > 0) {
      toast.error(
        `${blocking.length} finding${blocking.length === 1 ? "" : "s"} you re-classified still ` +
        "need an override reason. Showing them now.",
      );
      jumpToFirstIssue();
      return;
    }
    setSaving(true);
    try {
      await API.post(`/audit-runs/${auditRun.id}/findings/classify`, {
        findings: findings.map((f) => ({
          id: f.id,
          final_severity: severityOf(f),
          override_reason: f.override_reason || "",
        })),
      });
      await advancePhase(10);
      await refreshRun();
      navigate("/phase/10");
      toast.success("Classification finalized. Proceeding to Report.");
    } catch (e) {
      toast.error("Failed to finalize");
    }
    setSaving(false);
  };

  const heatmapColor = (s) => ({
    Critical: "bg-red-600 dark:bg-red-700", High: "bg-red-500 dark:bg-red-600", Medium: "bg-amber-500 dark:bg-amber-600",
    Low: "bg-blue-500 dark:bg-blue-600", Observation: "bg-slate-400 dark:bg-slate-600",
  }[s] || "bg-muted");
  const severityCardClass = (sev) =>
    sevCounts[sev] === 0
      ? "opacity-45 saturate-50 ring-1 ring-white/10"
      : "opacity-100 shadow-[0_14px_30px_rgba(15,23,42,0.22)]";

  const filterPill = (active) =>
    `h-7 px-2.5 text-xs rounded-full border transition-colors ${
      active
        ? "bg-indigo-600 text-white border-indigo-600"
        : "bg-transparent border-slate-200 dark:border-white/[0.12] text-muted-foreground hover:bg-slate-100 dark:hover:bg-white/[0.06]"
    }`;

  return (
    <PageLayout
      icon={CheckSquare}
      title="Phase 9: Classification Review"
      subtitle="Finalize risk classifications for all findings."
      actions={
        <>
          {isTeamMode && (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              <Users className="w-3 h-3 mr-1" /> Team Mode
            </Badge>
          )}
          <Button
            onClick={handleFinalize}
            disabled={saving || !hasAnyFindings}
            data-testid="finalize-classification-top-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-4 shadow-md shadow-emerald-600/30 active:scale-95 border-0"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
            Finalize &amp; Generate Report
          </Button>
        </>
      }
      data-testid="phase-9"
    >
      <Card>
        <CardHeader><CardTitle className="text-base">Risk Heatmap</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="severity-heatmap">
            {SEVERITIES.map((sev) => (
              <button
                key={sev}
                type="button"
                onClick={() => setSeverityFilter(severityFilter === sev ? "all" : sev)}
                className={`metric-card-anim rounded-xl p-4 text-center text-white transition-all ${heatmapColor(sev)} ${severityCardClass(sev)} ${
                  severityFilter === sev ? "ring-2 ring-offset-2 ring-indigo-500 ring-offset-background" : ""
                }`}
                title={`Show only ${sev} findings`}
              >
                <p className="text-3xl font-bold font-mono">{sevCounts[sev]}</p>
                <p className="text-xs font-semibold uppercase tracking-wider mt-1">{sev}</p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Click a severity to filter the list below.
          </p>
        </CardContent>
      </Card>

      {isTeamMode && consolidatedFindings.length > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />
              Team Consolidated Findings ({consolidatedFindings.length})
              <Badge variant="outline" className="ml-2 bg-emerald-500/10 text-emerald-400 text-xs">Pre-validated</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {consolidatedFindings.map((cf, idx) => (
              <div key={cf.id || idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-md bg-card">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant="outline" className={`text-xs shrink-0 ${
                    cf.severity === "Critical" ? "bg-red-600/10 text-red-600 border-red-600/30"
                      : cf.severity === "High" ? "bg-red-500/10 text-red-500 border-red-500/30"
                      : cf.severity === "Medium" ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
                      : cf.severity === "Low" ? "bg-blue-500/10 text-blue-500 border-blue-500/30"
                      : "bg-slate-500/10 text-slate-400 border-slate-500/30"
                  }`}>
                    {cf.severity}
                  </Badge>
                  <p className="text-xs font-medium text-slate-800 dark:text-slate-200 leading-normal">{(() => {
                    const t = cf.finding_title || "";
                    return t
                      .replace(/^SQL audit FAIL:\s*/i, "Violation Detected: ")
                      .replace(/^SQL audit PASS:\s*/i, "Control Passed: ")
                      .replace(/^SQL audit\s+\w+:\s*/i, "") || t;
                  })()}</p>
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap self-end sm:self-auto">Team validated</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {isTeamMode ? "Additional Solo Findings" : "Classification"} ({findings.length} findings)
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Filter bar. With hundreds of findings the list is unusable without
              a way to narrow it and a way to reach the ones that need action. */}
          {findings.length > 0 && (
            <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-card/95 backdrop-blur border-b border-slate-100 dark:border-white/[0.06] space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search title, control reference or narrative"
                    className="h-8 pl-8 text-xs"
                  />
                </div>

                <button type="button" className={filterPill(severityFilter === "all")} onClick={() => setSeverityFilter("all")}>
                  All {findings.length}
                </button>
                {SEVERITIES.map((sev) => (
                  <button key={sev} type="button" className={filterPill(severityFilter === sev)} onClick={() => setSeverityFilter(sev)}>
                    {sev} {sevCounts[sev]}
                  </button>
                ))}
                <button type="button" className={filterPill(changedOnly)} onClick={() => setChangedOnly((v) => !v)}>
                  Changed {changedCount}
                </button>
                <button
                  type="button"
                  className={`h-7 px-2.5 text-xs rounded-full border transition-colors ${
                    issuesOnly
                      ? "bg-red-600 text-white border-red-600"
                      : blocking.length > 0
                        ? "border-red-400 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                        : "border-slate-200 dark:border-white/[0.12] text-muted-foreground"
                  }`}
                  onClick={() => setIssuesOnly((v) => !v)}
                  disabled={blocking.length === 0 && !issuesOnly}
                >
                  Needs reason {blocking.length}
                </button>

                {(severityFilter !== "all" || issuesOnly || changedOnly || search) && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearFilters}>
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <Filter className="w-3 h-3" />
                <span>
                  Showing {visible.length ? safePage * PAGE_SIZE + 1 : 0}
                  &ndash;{safePage * PAGE_SIZE + visible.length} of {filtered.length}
                </span>

                <span className="mx-1 opacity-40">|</span>

                <Wand2 className="w-3 h-3" />
                <span>Apply to all {filtered.length} shown:</span>
                <Select value={bulkSeverity} onValueChange={setBulkSeverity}>
                  <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue placeholder="Set severity" /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={applyBulkSeverity} disabled={!bulkSeverity || filtered.length === 0}>
                  Apply
                </Button>

                <Input
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="Reason for changed findings"
                  className="h-7 w-[220px] text-xs"
                />
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={applyBulkReason} disabled={!bulkReason.trim()}>
                  Apply reason
                </Button>
              </div>

              {blocking.length > 0 && (
                <div className="flex items-center gap-2 text-[11px] text-red-500">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {blocking.length} finding{blocking.length === 1 ? "" : "s"} you re-classified
                    still need{blocking.length === 1 ? "s" : ""} an override reason.
                  </span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-red-500" onClick={jumpToFirstIssue}>
                    Take me there
                  </Button>
                </div>
              )}
            </div>
          )}

          {findings.length === 0 && !isTeamMode && (
            <div className="text-center py-6 text-muted-foreground min-h-[220px] flex flex-col justify-center">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No findings to classify.</p>
              <p className="text-xs mt-1">Generate findings from evidence evaluation first.</p>
            </div>
          )}
          {findings.length === 0 && isTeamMode && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No additional solo findings. Team findings above are ready for report.
            </p>
          )}
          {findings.length > 0 && filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-10">
              No findings match the current filters.{" "}
              <button type="button" className="underline" onClick={clearFilters}>Clear them</button>.
            </p>
          )}

          {visible.map((f) => {
            const displayTitle = f.title || f.finding_title || f.finding_text || f.finding_description || f.description || "Untitled Finding";
            const suggestedSev = suggestedOf(f);
            const currentSev = severityOf(f);
            const baseline = baselineSeverity[f.id];
            const auditorChanged = wasChangedByAuditor(f);
            const missingReason = needsReason(f);
            // The backend classified this differently from its own suggestion.
            // Worth showing, but it is not the auditor's override to justify.
            const agentAdjusted = baseline !== undefined && baseline !== suggestedSev;

            return (
              <div
                key={f.id}
                ref={(node) => { rowRefs.current[f.id] = node; }}
                className={`space-y-4 p-5 border rounded-xl bg-card shadow-sm transition-colors ${
                  missingReason
                    ? "border-red-400 dark:border-red-500/60 ring-1 ring-red-400/30"
                    : "border-slate-200 dark:border-white/[0.08]"
                }`}
                data-testid={`classify-${f.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-3 flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                      <span className="font-mono text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded shrink-0 w-fit">
                        {f.control_ref || formatAgentStyleIdentifier(f.control_id)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug">{displayTitle}</p>
                        {(f.evidence_narrative || f.summary || f.finding_text || f.finding_description) && (
                          <div className="mt-2 bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.05] p-3 rounded-lg shadow-inner">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">Evidence Narrative</p>
                            <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                              {f.evidence_narrative || f.summary || f.finding_text || f.finding_description}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 self-start md:self-auto w-full md:w-auto pt-2 md:pt-0">
                    <Select value={currentSev} onValueChange={(v) => updateSeverity(f.id, v)}>
                      <SelectTrigger className="w-full md:w-[140px] h-9 font-medium">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {auditorChanged && (
                      <button
                        type="button"
                        className="mt-1.5 text-[10px] text-muted-foreground underline w-full text-center md:text-right"
                        onClick={() => updateSeverity(f.id, baseline)}
                      >
                        Revert to {baseline}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.05]">
                  <Badge variant="outline" className="shrink-0 w-fit text-[10px] font-bold bg-slate-50 dark:bg-white/[0.02] border-slate-200 dark:border-white/[0.08] py-1">
                    Suggested: {suggestedSev}
                  </Badge>
                  {agentAdjusted && (
                    <Badge variant="outline" className="shrink-0 w-fit text-[10px] bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-300 py-1">
                      Agent set {baseline}
                    </Badge>
                  )}
                  {auditorChanged && (
                    <Badge variant="outline" className="shrink-0 w-fit text-[10px] bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300 py-1">
                      You changed {baseline} to {currentSev}
                    </Badge>
                  )}
                  <Input
                    value={f.override_reason || ""}
                    onChange={(e) => updateOverrideReason(f.id, e.target.value)}
                    className={`flex-1 h-9 text-xs bg-slate-50 dark:bg-white/[0.02] ${
                      missingReason ? "border-red-400 dark:border-red-500/60" : ""
                    }`}
                    placeholder={
                      auditorChanged
                        ? "Override reason is required because you changed the severity"
                        : "Optional auditor note"
                    }
                  />
                </div>
              </div>
            );
          })}

          {pageCount > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {safePage + 1} of {pageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>
                Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleFinalize} disabled={saving || !hasAnyFindings} data-testid="finalize-classification-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
          Finalize &amp; Generate Report
        </Button>
      </div>
    </PageLayout>
  );
}















// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { useAudit } from "../context/AuditContext";
// import { Button } from "../components/ui/button";
// import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
// import { Badge } from "../components/ui/badge";
// import { Input } from "../components/ui/input";
// import { toast } from "sonner";
// import API from "../lib/api";
// import { CheckSquare, ArrowRight, Loader2, Users, AlertTriangle } from "lucide-react";
// import { PageLayout } from "../components/PageLayout";

// const FINDING_REVIEW_STORAGE_PREFIX = "zentis:findingReviews:";
// const REVIEW_STATES = {
//   accepted: "accepted",
// };

// const formatAgentStyleIdentifier = (value) => {
//   const text = String(value || "").trim();
//   if (!text) return "-";

//   const teamSqlMatch = text.match(/^team_sql_([a-z_]+?)(?:_\d+)?(?:_[a-f0-9]{6,})?$/i);
//   if (teamSqlMatch?.[1]) return teamSqlMatch[1];

//   return text;
// };

// export default function Phase9Classification() {
//   const navigate = useNavigate();
//   const { auditRun, advancePhase, refreshRun, findings: contextFindings = [], fetchFindings } = useAudit();
//   const [findings, setFindings] = useState([]);
//   const [teamData, setTeamData] = useState(null);
//   const [saving, setSaving] = useState(false);
//   const [reviewedFindingMap, setReviewedFindingMap] = useState({});

//   const normalizeReviewStatus = (value) => String(value || "").trim().toLowerCase();

//   // Fetch team data for consolidated findings
//   const fetchTeamData = useCallback(async () => {
//     if (!auditRun?.id) return;
//     try {
//       const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
//       setTeamData(data);
//     } catch (e) {
//       console.error("Failed to fetch team data:", e);
//     }
//   }, [auditRun?.id]);

//   useEffect(() => { 
//     fetchTeamData();
//     if (contextFindings.length === 0) {
//       fetchFindings();
//     }
//   }, [fetchTeamData, contextFindings.length, fetchFindings]);

//   useEffect(() => {
//     if (contextFindings && contextFindings.length > 0) {
//       setFindings(contextFindings.filter((f) => {
//         if (f.suppressed) return false;
//         const reviewedStatus =
//           reviewedFindingMap[f.id]?.status ??
//           f.review_status ??
//           REVIEW_STATES.accepted;
//         return normalizeReviewStatus(reviewedStatus) === REVIEW_STATES.accepted;
//       }));
//     }
//   }, [contextFindings, reviewedFindingMap]);

//   useEffect(() => {
//     if (!auditRun?.id) return;
//     try {
//       const raw = localStorage.getItem(`${FINDING_REVIEW_STORAGE_PREFIX}${auditRun.id}`);
//       setReviewedFindingMap(raw ? JSON.parse(raw) : {});
//     } catch {
//       setReviewedFindingMap({});
//     }
//   }, [auditRun?.id]);
  
//   const isTeamMode = teamData?.audit_mode === "team";
//   const consolidatedFindings = teamData?.consolidated_findings || [];
//   const hasAnyFindings = findings.length > 0 || consolidatedFindings.length > 0;

//   const updateSeverity = (id, severity) => {
//     setFindings(prev => prev.map(f => f.id === id ? { ...f, final_severity: severity } : f));
//   };
//   const updateOverrideReason = (id, overrideReason) => {
//     setFindings((prev) => prev.map((f) => (f.id === id ? { ...f, override_reason: overrideReason } : f)));
//   };

//   const handleFinalize = async () => {
//     setSaving(true);
//     try {
//       const missingOverrideReason = findings.some((f) => {
//         const suggested = f.suggested_severity || f.severity || "Medium";
//         const finalSeverity = f.final_severity || suggested;
//         return finalSeverity !== suggested && !String(f.override_reason || "").trim();
//       });
//       if (missingOverrideReason) {
//         toast.error("Add an override reason whenever the final severity differs from the suggested severity.");
//         setSaving(false);
//         return;
//       }
//       await API.post(`/audit-runs/${auditRun.id}/findings/classify`, {
//         findings: findings.map(f => ({
//           id: f.id,
//           final_severity: f.final_severity || f.suggested_severity || f.severity || "Medium",
//           override_reason: f.override_reason || "",
//         }))
//       });
//       await advancePhase(10);
//       await refreshRun();
//       navigate("/phase/10");
//       toast.success("Classification finalized. Proceeding to Report.");
//     } catch (e) { toast.error("Failed to finalize"); }
//     setSaving(false);
//   };

//   const sevCounts = { High: 0, Medium: 0, Low: 0, Observation: 0 };
  
//   // Count solo findings
//   findings.forEach(f => {
//     const s = f.final_severity || f.suggested_severity || f.severity || "Medium";
//     if (sevCounts[s] !== undefined) sevCounts[s]++;
//   });
  
//   // Count team consolidated findings
//   consolidatedFindings.forEach(cf => {
//     const s = cf.severity;
//     if (sevCounts[s] !== undefined) sevCounts[s]++;
//   });

//   const heatmapColor = (s) => ({
//     "High": "bg-red-500 dark:bg-red-600", "Medium": "bg-amber-500 dark:bg-amber-600",
//     "Low": "bg-blue-500 dark:bg-blue-600", "Observation": "bg-slate-400 dark:bg-slate-600"
//   }[s] || "bg-muted");
//   const animatedSeverityCounts = {
//     High: sevCounts.High,
//     Medium: sevCounts.Medium,
//     Low: sevCounts.Low,
//     Observation: sevCounts.Observation,
//   };
//   const severityCardClass = (sev) =>
//     sevCounts[sev] === 0
//       ? "opacity-45 saturate-50 ring-1 ring-white/10"
//       : "opacity-100 shadow-[0_14px_30px_rgba(15,23,42,0.22)]";

//   return (
//     <PageLayout
//       icon={CheckSquare}
//       title="Phase 9: Classification Review"
//       subtitle="Finalize severity classifications for all findings."
//       actions={
//         <>
//           {isTeamMode && (
//             <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
//               <Users className="w-3 h-3 mr-1" /> Team Mode
//             </Badge>
//           )}
//           <Button onClick={handleFinalize} disabled={saving || !hasAnyFindings} data-testid="finalize-classification-top-btn">
//             {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
//             Finalize & Generate Report
//           </Button>
//         </>
//       }
//       data-testid="phase-9"
//       maxWidth="max-w-7xl"
//     >

//       {/* Heatmap */}
//       <Card>
//         <CardHeader><CardTitle className="text-base">Severity Heatmap</CardTitle></CardHeader>
//         <CardContent>
//           <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="severity-heatmap">
//             {Object.entries(sevCounts).map(([sev]) => (
//               <div key={sev} className={`metric-card-anim rounded-xl p-4 text-center text-white transition-all ${heatmapColor(sev)} ${severityCardClass(sev)}`}>
//                 <p className="text-3xl font-bold font-mono">{animatedSeverityCounts[sev]}</p>
//                 <p className="text-xs font-semibold uppercase tracking-wider mt-1">{sev}</p>
//               </div>
//             ))}
//           </div>
//         </CardContent>
//       </Card>
      
//       {/* Team Consolidated Findings - Read Only */}
//       {isTeamMode && consolidatedFindings.length > 0 && (
//         <Card className="border-emerald-500/30 bg-emerald-500/5">
//           <CardHeader>
//             <CardTitle className="text-base flex items-center gap-2">
//               <Users className="w-4 h-4 text-emerald-400" />
//               Team Consolidated Findings ({consolidatedFindings.length})
//               <Badge variant="outline" className="ml-2 bg-emerald-500/10 text-emerald-400 text-xs">Pre-validated</Badge>
//             </CardTitle>
//           </CardHeader>
//           <CardContent className="space-y-2">
//             {consolidatedFindings.map((cf, idx) => (
//               <div key={cf.id || idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-md bg-card">
//                 <div className="flex items-center gap-3 min-w-0">
//                   <Badge variant="outline" className={`text-xs shrink-0 ${
//                     cf.severity === 'High' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
//                     cf.severity === 'Medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' :
//                     cf.severity === 'Low' ? 'bg-blue-500/10 text-blue-500 border-blue-500/30' :
//                     'bg-slate-500/10 text-slate-400 border-slate-500/30'
//                   }`}>
//                     {cf.severity}
//                   </Badge>
//                   <p className="text-xs font-medium text-slate-800 dark:text-slate-200 leading-normal">{(() => {
//                       const t = cf.finding_title || "";
//                       return t
//                         .replace(/^SQL audit FAIL:\s*/i, "Violation Detected: ")
//                         .replace(/^SQL audit PASS:\s*/i, "Control Passed: ")
//                         .replace(/^SQL audit\s+\w+:\s*/i, "") || t;
//                     })()}</p>
//                 </div>
//                 <span className="text-[10px] text-muted-foreground whitespace-nowrap self-end sm:self-auto">Team validated</span>
//               </div>
//             ))}
//           </CardContent>
//         </Card>
//       )}

//       {/* Solo Findings Classification */}
//       <Card>
//         <CardHeader>
//           <CardTitle className="text-base">
//             {isTeamMode ? "Additional Solo Findings" : "Classification"} ({findings.length} findings)
//           </CardTitle>
//         </CardHeader>
//         <CardContent className={`space-y-3 ${findings.length === 0 ? "min-h-[260px] flex flex-col justify-center" : ""}`}>
//           {findings.length === 0 && !isTeamMode && (
//             <div className="text-center py-6 text-muted-foreground">
//               <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
//               <p className="text-sm">No findings to classify.</p>
//               <p className="text-xs mt-1">Generate findings from evidence evaluation first.</p>
//             </div>
//           )}
//           {findings.length === 0 && isTeamMode && (
//             <p className="text-xs text-muted-foreground text-center py-4">
//               No additional solo findings. Team findings above are ready for report.
//             </p>
//           )}
//           {[...findings]
//             .sort((a, b) => {
//               const hasNarrativeA = !!(a.evidence_narrative || a.summary || a.finding_text);
//               const hasNarrativeB = !!(b.evidence_narrative || b.summary || b.finding_text);
//               if (hasNarrativeA && !hasNarrativeB) return -1;
//               if (!hasNarrativeA && hasNarrativeB) return 1;
//               return 0;
//             })
//             .map(f => {
//             const displayTitle = f.title || f.finding_title || f.finding_text || f.finding_description || f.description || "Untitled Finding";
//             const suggestedSev = f.suggested_severity || f.severity || "Medium";
//             const currentSev = f.final_severity || suggestedSev;
            
//             return (
//             <div key={f.id} className="space-y-4 p-5 border border-slate-200 dark:border-white/[0.08] rounded-xl bg-card shadow-sm" data-testid={`classify-${f.id}`}>
//               <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
//                 <div className="space-y-3 flex-1 min-w-0">
//                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
//                       <span className="font-mono text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded shrink-0 w-fit">
//                         {f.control_ref || formatAgentStyleIdentifier(f.control_id)}
//                       </span>
//                       <div className="flex-1 min-w-0">
//                           <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug">{displayTitle}</p>
//                           {(f.evidence_narrative || f.summary || f.finding_text || f.finding_description) && (
//                              <div className="mt-2 bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.05] p-3 rounded-lg shadow-inner">
//                                 <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">Evidence Narrative</p>
//                                 <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
//                                    {f.evidence_narrative || f.summary || f.finding_text || f.finding_description}
//                                 </p>
//                              </div>
//                           )}
//                       </div>
//                    </div>
//                 </div>
//                 <div className="shrink-0 self-start md:self-auto w-full md:w-auto pt-2 md:pt-0">
//                   <Select
//                     value={currentSev}
//                     onValueChange={v => updateSeverity(f.id, v)}
//                   >
//                     <SelectTrigger className="w-full md:w-[140px] h-9 font-medium">
//                       <SelectValue />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="High">High</SelectItem>
//                       <SelectItem value="Medium">Medium</SelectItem>
//                       <SelectItem value="Low">Low</SelectItem>
//                       <SelectItem value="Observation">Observation</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>
//               </div>
              
//               <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-slate-100 dark:border-white/[0.05]">
//                 <Badge variant="outline" className="shrink-0 w-fit text-[10px] font-bold bg-slate-50 dark:bg-white/[0.02] border-slate-200 dark:border-white/[0.08] py-1">
//                   Suggested: {suggestedSev}
//                 </Badge>
//                 <Input
//                   value={f.override_reason || ""}
//                   onChange={(e) => updateOverrideReason(f.id, e.target.value)}
//                   className="flex-1 h-9 text-xs bg-slate-50 dark:bg-white/[0.02]"
//                   placeholder={
//                     currentSev !== suggestedSev
//                       ? "Override reason is required when severity changes"
//                       : "Optional auditor note"
//                   }
//                 />
//               </div>
//             </div>
//           )})}
//         </CardContent>
//       </Card>

//       <div className="flex justify-end">
//         <Button onClick={handleFinalize} disabled={saving || !hasAnyFindings} data-testid="finalize-classification-btn">
//           {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
//           Finalize & Generate Report
//         </Button>
//       </div>
//     </PageLayout>
//   );
// }
